import clsx from 'clsx';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';

const SwitchContext = createContext<{
  value: string | null;
  setValue: (value: string) => void;
} | null>(null);

interface SwitchProps {
  children: React.ReactNode;
  name?: string;
  size?: 'small' | 'medium' | 'large';
  style?: React.CSSProperties;
  className?: string;
  onValueChange?: (value: string) => void;
}

export const Switch = ({
  children,
  name = 'default',
  size = 'medium',
  style,
  className,
  onValueChange,
}: SwitchProps) => {
  const [value, setValue] = useState<string | null>(null);

  const handleSetValue = useCallback(
    (newValue: string) => {
      setValue(newValue);
      onValueChange?.(newValue);
    },
    [onValueChange],
  );

  return (
    <SwitchContext.Provider value={{ value, setValue: handleSetValue }}>
      <div
        className={clsx(
          'flex bg-background-100 p-px border border-gray-alpha-400 rounded-full',
          size === 'small' && 'h-7',
          size === 'medium' && 'h-10',
          size === 'large' && 'h-12',
          className,
        )}
        style={style}
      >
        {React.Children.map(children, (child) =>
          React.cloneElement(child as React.ReactElement<SwitchControlProps>, {
            size,
            name,
          }),
        )}
      </div>
    </SwitchContext.Provider>
  );
};

interface SwitchControlProps {
  label?: string;
  value: string;
  defaultChecked?: boolean;
  disabled?: boolean;
  name?: string;
  size?: 'small' | 'medium' | 'large';
  icon?: React.ReactNode;
}

const SwitchControl = ({
  label,
  value,
  defaultChecked,
  disabled = false,
  name,
  size = 'medium',
  icon,
}: SwitchControlProps) => {
  const context = useContext(SwitchContext);
  const checked = value === context?.value;
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (defaultChecked && !hasInitialized.current) {
      context?.setValue(value);
      hasInitialized.current = true;
    }
  }, [defaultChecked, value, context]);

  return (
    <label
      className={clsx(
        'flex flex-1 h-full',
        disabled && 'cursor-not-allowed pointer-events-none',
      )}
      onClick={() => context?.setValue(value)}
    >
      <input
        type="radio"
        name={name}
        value={value}
        disabled={disabled}
        checked={checked}
        readOnly
        className="hidden"
      />
      <span
        className={twMerge(
          clsx(
            'flex items-center justify-center flex-1 cursor-pointer font-medium font-sans duration-150',
            checked
              ? 'bg-gray-200 text-gray-1000 fill-gray-1000 rounded-full dark:bg-neutral-950 text-foreground'
              : 'text-gray-900 dark:text-gray-50 hover:text-gray-1000 fill-gray-900 hover:fill-gray-1000',
            disabled && 'text-gray-800 fill-gray-800',
            !icon && size === 'small' && 'text-xs px-3',
            !icon && size === 'medium' && 'text-sm px-3',
            !icon && size === 'large' && 'text-base px-4',
            icon && size === 'small' && 'py-1 px-2',
            icon && size === 'medium' && 'py-2 px-3',
            icon && size === 'large' && 'p-3',
          ),
        )}
      >
        {icon ? (
          <span className={clsx(size === 'large' && 'scale-125')}>{icon}</span>
        ) : (
          label
        )}
      </span>
    </label>
  );
};

Switch.Control = SwitchControl;
